// Stock-up campaign: countdown to the order cutoff.
// After the cutoff, elements with [data-stockup-hide-after-cutoff] are hidden.
(() => {
  const pad = (n) => String(n).padStart(2, '0');

  const markClosed = () => {
    document.documentElement.classList.add('stockup-closed');
  };

  if (!customElements.get('stockup-countdown')) {
    customElements.define(
      'stockup-countdown',
      class extends HTMLElement {
        connectedCallback() {
          this.deadline = Date.parse(this.dataset.deadline);
          if (Number.isNaN(this.deadline)) return;
          this.units = {};
          this.querySelectorAll('[data-unit]').forEach((el) => (this.units[el.dataset.unit] = el));
          this.tick();
          this.timer = setInterval(() => this.tick(), 1000);
        }

        disconnectedCallback() {
          clearInterval(this.timer);
        }

        tick() {
          const left = Math.max(0, Math.floor((this.deadline - Date.now()) / 1000));
          const u = this.units;
          if (u.d) u.d.textContent = Math.floor(left / 86400);
          if (u.h) u.h.textContent = pad(Math.floor((left % 86400) / 3600));
          if (u.m) u.m.textContent = pad(Math.floor((left % 3600) / 60));
          if (u.s) u.s.textContent = pad(left % 60);
          if (left === 0) {
            clearInterval(this.timer);
            markClosed();
          }
        }
      }
    );
  }
})();

// "Stock up and save" packs (product page) and ready-made packs (stock-up page): add the
// items in one request, then refresh the cart drawer. data-items is a JSON list of {id, quantity};
// otherwise data-variant-id and data-quantity.
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-stockup-add]');
  if (!button) return;
  event.preventDefault();

  const drawer = document.querySelector('cart-drawer');
  const items = button.dataset.items
    ? JSON.parse(button.dataset.items)
    : [{ id: Number(button.dataset.variantId), quantity: Number(button.dataset.quantity) }];
  const body = { items };
  if (drawer?.getSectionsToRender) {
    body.sections = drawer.getSectionsToRender().map((s) => s.id);
    body.sections_url = window.location.pathname;
  }

  button.setAttribute('aria-busy', 'true');
  button.disabled = true;
  try {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const state = await res.json();
    if (!res.ok || state.status) throw new Error(state.description || state.message || 'Could not add to cart');

    if (drawer?.renderContents && state.sections) {
      // As the theme's product form does: a drawer opened from an empty cart keeps its
      // empty-state layout (no header, one grid row) unless the class is cleared first.
      drawer.classList.remove('is-empty');
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
      drawer.renderContents({ ...state, id: items[0].id, items: cart.items });
      window.publish?.(window.PUB_SUB_EVENTS?.cartUpdate, { source: 'stockup-upsell', cartData: cart });
    } else {
      window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
    }
  } catch (error) {
    alert(error.message);
  } finally {
    button.removeAttribute('aria-busy');
    button.disabled = false;
  }
});

// Bundle page: recount the bags whenever the "(N selected)" counter of select-your-stash
// changes, and update the tier bar. Keeps the compiled selectYourStash.js untouched.
(() => {
  const init = () => {
    document.querySelectorAll('[data-stockup-tiers][data-live]').forEach((bar) => {
      const root = bar.closest('.select-your-stash');
      const counter = root?.querySelector('.select-your-stash__cart__selected');
      if (!counter) return;
      const t2 = Number(bar.dataset.t2);
      const t3 = Number(bar.dataset.t3);
      const p2 = bar.dataset.p2;
      const p3 = bar.dataset.p3;
      const message = bar.querySelector('[data-stockup-message]');
      const fill = bar.querySelector('[data-stockup-fill]');

      const wanted = Number(new URLSearchParams(window.location.search).get('bags')) || 0;
      // Count only bags (the product types the discount counts); the bundle also sells carts.
      const bagIds = new Set((bar.dataset.bagIds || '').split(',').filter(Boolean));
      const countBags = () => {
        if (!bagIds.size) {
          const match = counter.textContent.match(/\d+/);
          return match && !counter.classList.contains('hidden') ? Number(match[0]) : 0;
        }
        let bags = 0;
        root.querySelectorAll('.select-your-stash__cart__item[data-product-id]').forEach((item) => {
          if (!bagIds.has(item.dataset.productId)) return;
          const qty = parseInt(item.querySelector('.select-your-stash__cart__item__title')?.textContent, 10);
          bags += Number.isFinite(qty) ? qty : 0;
        });
        return bags;
      };

      const render = () => {
        const count = countBags();
        if (count >= t3) {
          message.innerHTML = `<span class="stockup-nowrap">6-month</span> pricing unlocked: you save ${p3}%`;
        } else if (count >= t2) {
          message.innerHTML = `<span class="stockup-nowrap">3-month</span> pricing unlocked. Add ${t3 - count} more for <span class="stockup-nowrap">6-month</span> pricing (${p3}% off)`;
        } else if (wanted >= t3) {
          // Came from "Build my 6-month stash" (?bags=6): aim straight for that tier
          const need = t3 - count;
          message.innerHTML = `Add ${need} more ${need === 1 ? 'bag' : 'bags'} for <span class="stockup-nowrap">6-month</span> pricing (${p3}% off)`;
        } else {
          const need = t2 - count;
          message.innerHTML = `Add ${need} more ${need === 1 ? 'bag' : 'bags'} for <span class="stockup-nowrap">3-month</span> pricing (${p2}% off)`;
        }
        fill.style.width = `${Math.min(100, (count / t3) * 100)}%`;
        bar.querySelector('[data-stockup-stop="2"]')?.classList.toggle('is-reached', count >= t2);
        bar.querySelector('[data-stockup-stop="3"]')?.classList.toggle('is-reached', count >= t3);
      };

      // The panel's own totals know the subscription price only. Add the stock-up discount the
      // Toolkit function gives at checkout: per bag, the discount rounded down to the cent.
      const cents = (text) => {
        const n = Number(String(text || '').replace(/[^0-9.]/g, ''));
        return Number.isFinite(n) ? Math.round(n * 100) : 0;
      };
      const money = (c) => (window.formatMoney ? window.formatMoney(c / 100) : `$${(c / 100).toFixed(2)}`);
      const q = (sel) => root.querySelector(sel);
      const applyTotals = (bags) => {
        const pct = bags >= t3 ? Number(p3) : bags >= t2 ? Number(p2) : 0;
        let off = 0;
        root.querySelectorAll('.select-your-stash__cart__item[data-product-id]').forEach((item) => {
          if (!pct || !bagIds.has(item.dataset.productId)) return;
          const qty = parseInt(item.querySelector('.select-your-stash__cart__item__title')?.textContent, 10) || 0;
          const line = cents(item.querySelector('.select-your-stash__cart__item__price')?.textContent);
          if (!qty || !line) return;
          off += Math.floor((Math.round(line / qty) * pct) / 100) * qty;
        });
        const subtotalEl = q('.subtotal .select-your-stash__cart__totals__item__value');
        const savingsEl = q('.savings .select-your-stash__cart__totals__item__value');
        const totalEl = q('.select-your-stash__cart__checkout__total');
        const strikeEl = q('.select-your-stash__cart__checkout__subtotal');
        if (!subtotalEl || !totalEl) return;
        // Runs right after the page rewrote its totals (the counter is rewritten in the same
        // step), so the total on screen is always the page's own, never already reduced.
        const subtotal = cents(subtotalEl.textContent);
        const total = Math.max(0, cents(totalEl.textContent) - off);
        // formatMoney() prints FREE for 0, which read as "Savings: FREE" and "Checkout • FREE"
        const plain = (c) => (c > 0 ? money(c) : '$0.00');
        totalEl.textContent = plain(total);
        if (savingsEl) savingsEl.textContent = plain(subtotal - total);
        if (strikeEl) strikeEl.textContent = subtotal > total ? money(subtotal) : '';
        updateRewards(total / 100);
      };

      // Same as the page's reward tracker, fed with the total after the stock-up discount,
      // because the stash rewards are order discounts that Shopify applies after it.
      const updateRewards = (total) => {
        let rewards = [];
        try {
          rewards = JSON.parse(root.dataset.rewardsJson || '[]') || [];
        } catch (e) {
          rewards = [];
        }
        const list = q('.select-your-stash__cart__tiered-reward-tracker__rewards');
        const line = q('.select-your-stash__cart__tiered-reward-tracker__line');
        if (!rewards.length || !list || !line) return;
        list.querySelector('.starting-reward')?.classList.toggle('active', total > 0);
        let activeIndex = -1;
        rewards.forEach((reward, i) => {
          const isActive = total >= reward.cartSize;
          list.querySelector(`[data-reward-index="${i}"]`)?.classList.toggle('active', isActive);
          if (isActive) activeIndex = i;
        });
        list.style.setProperty('--current-reward-index', activeIndex);
        const n = rewards.length;
        let progress = 0;
        if (total <= rewards[0].cartSize) progress = total / rewards[0].cartSize / n;
        else if (total > rewards[n - 1].cartSize) progress = 1;
        else {
          for (let i = 1; i < n; i++) {
            const prev = rewards[i - 1].cartSize;
            const next = rewards[i].cartSize;
            if (total <= next) {
              progress = i / n + (total - prev) / (next - prev) / n;
              break;
            }
          }
        }
        line.style.setProperty('--progress', progress);
      };

      const update = () => {
        render();
        applyTotals(countBags());
      };
      new MutationObserver(update).observe(counter, { childList: true, characterData: true, subtree: true, attributes: true });
      update();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

// Stock-up page: on phones, show the sticky action once the hero button has scrolled away.
(() => {
  const sticky = document.querySelector('[data-stockup-sticky]');
  const heroCta = document.querySelector('[data-stockup-hero-cta]') || document.querySelector('.new-hero-design .new-button');
  if (!sticky || !heroCta || !('IntersectionObserver' in window)) return;
  new IntersectionObserver(([entry]) => {
    sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
  }).observe(heroCta);
})();
